import { useMemo } from 'react'
import { fbt } from 'fbtee'
import { Check } from 'lucide-react'
import { useUserProfiles } from '../../hooks/useUserProfiles'
import { UserRow } from '../UserRow'
import { placeholders } from '../../lib/skeletonUtils'
import { cn, compareByDisplayName } from '../../lib/utils'
import type { Group } from '../../types'

interface Props {
  group: Group
  currentUserId: string
  selected: Set<string>
  onToggle: (userId: string) => void
}

// The current user's own row is locked on: unchecking yourself mid-edit would
// strand the totem the moment you save. Another editor can still remove you.
export function TotemEditorsPicker({ group, currentUserId, selected, onToggle }: Props) {
  const profiles = useUserProfiles(group.memberIds)
  const loading = group.memberIds.some(uid => !profiles[uid])
  const members = useMemo(
    () =>
      [...group.memberIds].sort((a, b) => compareByDisplayName(a, b, profiles, currentUserId)),
    [group.memberIds, currentUserId, profiles],
  )

  return (
    <div>
      <span className="text-sm font-medium">
        {fbt('Who can manage this totem', 'Label above the list of group members who may edit a totem and mark it at sets')}
      </span>
      <ul className="mt-2 flex flex-col gap-1.5 max-h-52 overflow-y-auto" data-testid="totem-editors">
        {loading
          ? placeholders(Math.min(group.memberIds.length, 4)).map((_, i) => <UserRow key={i} loading />)
          : members.map(uid => {
              const locked = uid === currentUserId
              const on = selected.has(uid)
              const toggle = locked ? undefined : () => onToggle(uid)
              return (
                <UserRow
                  key={uid}
                  profile={profiles[uid]}
                  name={profiles[uid]?.displayName ?? ''}
                  isYou={locked}
                  onClick={toggle}
                  action={
                    <button
                      type="button"
                      onClick={toggle}
                      disabled={locked}
                      aria-pressed={on}
                      aria-label={profiles[uid]?.displayName ?? ''}
                      className={cn('p-1 rounded-md', !locked && 'cursor-pointer hover:bg-secondary')}
                    >
                      <Check className={cn('size-4', on ? 'text-primary' : 'opacity-0')} />
                    </button>
                  }
                />
              )
            })}
      </ul>
    </div>
  )
}
